import React, { useState } from 'react';
import { Brain, Sparkles, Calendar, Trophy, Users, RefreshCw, AlertCircle, X } from 'lucide-react';

const NextGamePrediction = () => {
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadPrediction = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch('/api/ai/predict', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!res.ok) {
        throw new Error('Prediction failed');
      }
      
      const data = await res.json();
      setPrediction(data);
      setHasLoaded(true);
      setShowPopup(true);
    } catch (err) {
      console.error('Prediction error:', err);
      setError('Ennustus epäonnistui');
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const handleBarClick = () => {
    if (loading) return;
    
    if (hasLoaded) {
      setShowPopup(!showPopup);
    } else {
      loadPrediction();
    }
  };

  const handleRefresh = (e) => {
    e.stopPropagation();
    loadPrediction();
  };

  const closePopup = (e) => {
    e.stopPropagation();
    setShowPopup(false);
  };

  return (
    <div className="oracle-container">
      {/* Compact Bar */}
      <div 
        className={`oracle-bar ${loading ? 'loading' : ''} ${hasLoaded ? 'has-data' : ''}`}
        onClick={handleBarClick}
      >
        <div className="oracle-bar-content">
          <div className={`oracle-icon ${loading ? 'spinning' : ''}`}>
            <Brain size={18} />
            {hasLoaded && !loading && <Sparkles size={10} className="sparkle-mini" />}
          </div>
          <span className="oracle-text">
            {loading ? (
              <>Analysoidaan todennäköisyysmatriisia...</>
            ) : hasLoaded && prediction?.games ? (
              <>🔮 Tuleva pelipäivä: {prediction.games.length} peliä ennustettu</>
            ) : (
              <>🔮 Kysy Oraakkelilta tuleva pelipäivä</>
            )}
          </span>
          {hasLoaded && !loading && (
            <button className="oracle-refresh" onClick={handleRefresh} title="Uusi ennustus">
              <RefreshCw size={14} />
            </button>
          )}
        </div>
        
        {/* Loading progress bar */}
        {loading && <div className="oracle-progress" />}
      </div>

      {/* Popup with full prediction */}
      {showPopup && (hasLoaded || error) && (
        <div className="oracle-popup">
          <button className="popup-close" onClick={closePopup}>
            <X size={16} />
          </button>
          
          {error ? (
            <div className="popup-error">
              <AlertCircle size={20} />
              <span>{error}</span>
              <button onClick={handleRefresh} className="retry-btn">
                <RefreshCw size={14} /> Yritä uudelleen
              </button>
            </div>
          ) : prediction && (
            <>
              <div className="popup-header">
                <Brain size={20} />
                <span>Oraakkelin ennustus</span>
                <span className="powered-by">Gemini AI</span>
              </div>
              
              <div className="popup-date">
                <Calendar size={14} />
                <span>Tuleva pelipäivä</span>
              </div>
              
              {/* 4 Games Grid */}
              <div className="games-grid">
                {prediction.games?.map((game, index) => (
                  <div key={index} className="game-prediction-card">
                    <div className="game-number">{index + 1}</div>
                    <div className="game-info">
                      <span className="game-name">{game.gameName}</span>
                      <div className="game-players">
                        <Users size={12} />
                        <span>{game.players?.join(', ') || '-'}</span>
                      </div>
                      <div className="game-winner">
                        <Trophy size={12} />
                        <span>{game.winner}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {prediction.rationale && (
                <div className="popup-rationale">
                  <span className="rationale-label">💭 Perustelu:</span>
                  <p>{prediction.rationale}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <style>{`
        .oracle-container {
          position: relative;
          margin-bottom: 1rem;
        }

        .oracle-bar {
          background: linear-gradient(90deg, #1a1a2e 0%, #16213e 100%);
          border: 1px solid rgba(102, 126, 234, 0.3);
          border-radius: 8px;
          padding: 0.75rem 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .oracle-bar:hover {
          border-color: rgba(102, 126, 234, 0.6);
          box-shadow: 0 0 15px rgba(102, 126, 234, 0.2);
        }

        .oracle-bar.loading {
          cursor: wait;
        }

        .oracle-bar.has-data {
          border-color: rgba(118, 75, 162, 0.5);
        }

        .oracle-bar-content {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .oracle-icon {
          width: 32px;
          height: 32px;
          background: linear-gradient(135deg, #667eea, #764ba2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          position: relative;
          flex-shrink: 0;
        }

        .oracle-icon.spinning {
          animation: spin 1.5s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .sparkle-mini {
          position: absolute;
          top: -2px;
          right: -2px;
          color: #ffd700;
        }

        .oracle-text {
          flex: 1;
          color: #ccc;
          font-size: 0.9rem;
        }

        .oracle-bar.loading .oracle-text {
          color: #667eea;
        }

        .oracle-bar.has-data .oracle-text {
          color: #e0e0e0;
        }

        .oracle-refresh {
          background: rgba(255,255,255,0.1);
          border: none;
          color: #888;
          padding: 0.4rem;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .oracle-refresh:hover {
          background: rgba(102, 126, 234, 0.3);
          color: #667eea;
        }

        .oracle-progress {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 2px;
          background: linear-gradient(90deg, #667eea, #764ba2);
          animation: progress 2s ease-in-out infinite;
        }

        @keyframes progress {
          0% { width: 0%; left: 0; }
          50% { width: 100%; left: 0; }
          100% { width: 0%; left: 100%; }
        }

        /* Popup */
        .oracle-popup {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          right: 0;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          border: 1px solid rgba(102, 126, 234, 0.5);
          border-radius: 12px;
          padding: 1.25rem;
          z-index: 100;
          box-shadow: 0 10px 40px rgba(0,0,0,0.5);
          animation: slideDown 0.2s ease-out;
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .popup-close {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          background: rgba(255,255,255,0.1);
          border: none;
          color: #888;
          padding: 0.3rem;
          border-radius: 50%;
          cursor: pointer;
        }

        .popup-close:hover {
          background: rgba(255,255,255,0.2);
          color: #fff;
        }

        .popup-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #667eea;
          margin-bottom: 1rem;
          font-weight: 600;
        }

        .popup-header .powered-by {
          margin-left: auto;
          font-size: 0.65rem;
          color: #666;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-weight: normal;
        }

        .popup-date {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #888;
          font-size: 0.85rem;
          margin-bottom: 1rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid rgba(255,255,255,0.1);
        }

        /* Games Grid */
        .games-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        @media (max-width: 500px) {
          .games-grid {
            grid-template-columns: 1fr;
          }
        }

        .game-prediction-card {
          background: rgba(0,0,0,0.2);
          border-radius: 8px;
          padding: 0.75rem;
          display: flex;
          gap: 0.75rem;
          border: 1px solid rgba(102, 126, 234, 0.2);
          transition: all 0.2s;
        }

        .game-prediction-card:hover {
          border-color: rgba(102, 126, 234, 0.4);
          background: rgba(0,0,0,0.3);
        }

        .game-number {
          width: 24px;
          height: 24px;
          background: linear-gradient(135deg, #667eea, #764ba2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: bold;
          color: white;
          flex-shrink: 0;
        }

        .game-info {
          flex: 1;
          min-width: 0;
        }

        .game-info .game-name {
          display: block;
          font-weight: 600;
          color: #e0e0e0;
          font-size: 0.9rem;
          margin-bottom: 0.25rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .game-players {
          display: flex;
          align-items: center;
          gap: 0.3rem;
          color: #888;
          font-size: 0.75rem;
          margin-bottom: 0.2rem;
        }

        .game-players span {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .game-winner {
          display: flex;
          align-items: center;
          gap: 0.3rem;
          color: #ffd700;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .popup-rationale {
          background: rgba(0,0,0,0.2);
          border-radius: 8px;
          padding: 0.75rem;
        }

        .rationale-label {
          font-size: 0.75rem;
          color: #888;
          display: block;
          margin-bottom: 0.25rem;
        }

        .popup-rationale p {
          margin: 0;
          font-style: italic;
          color: #bbb;
          font-size: 0.85rem;
          line-height: 1.4;
        }

        /* Error state */
        .popup-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          color: #e74c3c;
          text-align: center;
        }

        .retry-btn {
          background: rgba(231, 76, 60, 0.2);
          border: 1px solid rgba(231, 76, 60, 0.5);
          color: #e74c3c;
          padding: 0.4rem 0.8rem;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.3rem;
          font-size: 0.8rem;
        }

        .retry-btn:hover {
          background: rgba(231, 76, 60, 0.3);
        }
      `}</style>
    </div>
  );
};

export default NextGamePrediction;
